import { FormConfig, FormField } from 'components/common/form/FormConfig';
import { useCallback, useMemo } from 'react';
import i18n from 'i18n';
import { useFormikContext } from 'formik';
import { OutdoorType } from 'core/creative/Creative';
import { validateEmpty, validateValueInOptions } from 'utils/ValidateUtils';
import { OutdoorCreativeFields } from 'core/creative/OutdoorCreative';
import { OutdoorFormModel } from '../OutdoorFormModel';

export const useOutdoorBasicFields = <T extends OutdoorFormModel<T>>(
  model: T
): FormField[] => {
  const {
    creativeSizes,
    getOutdoorTypeOptions,
    getDefaultDuration,
    splicedCreativeSize,
    fixedCreativeSize
  } = model;

  const { values, setValues } = useFormikContext<OutdoorCreativeFields>();
  const isEditing = !!values.creativeId;

  const outdoorTypeOptions = useMemo(() => {
    return getOutdoorTypeOptions();
  }, [getOutdoorTypeOptions]);

  const onChangeOutdoorType = useCallback(
    (type: OutdoorType) => {
      setValues((prev) => {
        const oldDuration = prev.typeProperties.duration;
        const newValues = {
          ...prev,
          typeProperties: {
            ...prev.typeProperties,
            duration: getDefaultDuration(type, oldDuration),
            outdoorType: type
          },
          medias: {}
        } as OutdoorCreativeFields;
        return newValues;
      });
    },
    [setValues, getDefaultDuration]
  );

  const fields = useMemo(() => {
    const fieldBuilder = new FormConfig.FieldsBuilder();

    // 素材尺寸與素材類型各自決定要不要給選：編輯時兩者都鎖成唯讀，但尺寸還有一種
    // 「渠道就只有一個尺寸」的情況（電子紙），建立時也只給看。
    if (creativeSizes) {
      if (isEditing || fixedCreativeSize) {
        // 拼接素材的原始尺寸不在渠道回的尺寸清單裡，補進選項驗值才不會誤判。
        // splicedCreativeSize 由 cropInfo 算出（getFormModelData 時填），沒有
        // cropInfo 的舊素材回填的是檔案尺寸，不需要補選項。
        const creativeSizeOptions = splicedCreativeSize
          ? [
              ...creativeSizes,
              { label: splicedCreativeSize, value: splicedCreativeSize }
            ]
          : creativeSizes;
        fieldBuilder.addFormikLabel({
          label: i18n.t('creativeSetupFlow.labels.creativeSize'),
          name: 'typeProperties.creativeSize',
          validate: (value: string) =>
            validateValueInOptions(creativeSizeOptions, value)
        });
      } else {
        fieldBuilder.addFormikSelect({
          label: i18n.t('creativeSetupFlow.labels.creativeSize'),
          name: 'typeProperties.creativeSize',
          simpleValue: true,
          options: creativeSizes,
          validate: validateEmpty
        });
      }
    }

    if (isEditing) {
      fieldBuilder.addFormikLabel({
        label: i18n.t('creativeSetupFlow.labels.outdoorType'),
        name: 'typeProperties.outdoorType',
        // 唯讀顯示直接沿用建立時那份 options 的 label，渠道自己的講法（電子紙把
        // 「圖 + 音」講成「圖片」）才不會只在建立時對、編輯時又跳回共用文字
        // （POS-8070）。找不到對應選項才退回共用 key。
        formatter: (value) =>
          outdoorTypeOptions.find((option) => option.value === value)?.label ??
          i18n.t(`outdoorForm.labels.outdoorType${value}`),
        validate: (value: string) =>
          validateValueInOptions(outdoorTypeOptions, value)
      });
    } else {
      fieldBuilder.addFormikSelect({
        label: i18n.t('creativeSetupFlow.labels.outdoorType'),
        name: 'typeProperties.outdoorType',
        simpleValue: true,
        options: outdoorTypeOptions,
        onChange: onChangeOutdoorType,
        validate: (value: string) =>
          validateValueInOptions(outdoorTypeOptions, value)
      });
    }

    return fieldBuilder.build();
  }, [
    creativeSizes,
    isEditing,
    fixedCreativeSize,
    outdoorTypeOptions,
    splicedCreativeSize,
    onChangeOutdoorType
  ]);

  return fields;
};
